<template>
  <div class="page-container">
    <div class="main-content">
      <div class="page-inner-box">
        <h1 class="page-title">确认订单</h1>

        <!-- 收货地址 (如果有实物商品) -->
        <div v-if="hasPhysicalItems" class="section-box">
          <div class="section-title">收货地址</div>
          <div class="address-list">
            <div 
              v-for="addr in addresses" 
              :key="addr.id" 
              class="address-item"
              :class="{ active: selectedAddressId === addr.id }"
              @click="selectedAddressId = addr.id"
            >
              <div class="addr-name">{{ addr.name }} {{ addr.phone }}</div>
              <div class="addr-detail">{{ addr.province }}{{ addr.city }}{{ addr.district }}{{ addr.detail }}</div>
              <i class="el-icon-check check-icon" v-if="selectedAddressId === addr.id"></i>
            </div>
            <div class="address-item add-new" @click="goToAddressManage">
              <i class="el-icon-plus"></i>
              <span>管理地址</span>
            </div>
          </div>
        </div>

        <!-- 商品列表 -->
        <div class="section-box">
          <div class="section-title">商品信息</div>
          <div class="order-items">
            <div v-for="item in checkoutItems" :key="item.id" class="order-item">
              <img :src="item.image" class="item-img" alt="">
              <div class="item-info">
                <div class="item-name">{{ item.title }}</div>
                <div class="item-price">¥{{ item.price }} x {{ item.quantity }}</div>
              </div>
              <div class="item-total">¥{{ (item.price * item.quantity).toFixed(2) }}</div>
              <div class="item-remove" @click="removeItem(item.id)">
                <i class="el-icon-delete"></i>
              </div>
            </div>
          </div>
        </div>

        <!-- 优惠券/支付方式 -->
        <div class="section-box">
          <div class="section-title">支付方式</div>
          <div class="pay-methods">
            <div 
              class="pay-method" 
              :class="{ active: payType === 1 }"
              @click="payType = 1"
            >
              <img src="@/assets/image/weixin.png" alt="微信支付">
              <span>微信支付</span>
            </div>
            <div 
              class="pay-method" 
              :class="{ active: payType === 2 }"
              @click="payType = 2"
            >
              <img src="@/assets/image/zhifubao.png" alt="支付宝支付">
              <span>支付宝支付</span>
            </div>
          </div>
        </div>

        <!-- 结算栏 -->
        <div class="checkout-bar">
          <div class="price-info">
            <span>共 {{ totalQuantity }} 件，</span>
            <span>合计：</span>
            <span class="total-price">¥{{ totalAmount.toFixed(2) }}</span>
          </div>
          <el-button type="primary" size="large" :loading="submitting" @click="submitOrder">提交订单</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getMyAddresses } from '@/api/authApi'
import { getCartList, checkoutCart } from '@/api/cartApi'

export default {
  name: 'CartCheckout',
  data() {
    return {
      checkoutItems: [],
      addresses: [],
      selectedAddressId: null,
      payType: 1, // Default WeChat
      submitting: false,
      loading: false
    }
  },
  computed: {
    hasPhysicalItems() {
      // productType: 1=Physical, 2=Virtual

      return this.checkoutItems.some(item => item.productType === 1 || item.itemType === 2)
    },
    totalQuantity() {
      return this.checkoutItems.reduce((sum, item) => sum + item.quantity, 0)
    },
    totalAmount() {
      return this.checkoutItems.reduce((sum, item) => sum + (item.price * item.quantity), 0)
    }
  },
  created() {
    this.init()
  },
  methods: {
    async init() {
      // Get cartIds from localStorage
      let cartIds = []
      try {
        const stored = localStorage.getItem('checkoutCartIds')
        if (stored) {
          cartIds = JSON.parse(stored)
        }
      } catch (e) {
        console.error(e)
      }

      if (!cartIds || cartIds.length === 0) {
        this.$message.warning('没有选择商品')
        this.$router.push('/cart')
        return
      }


      this.loading = true
      try {
        const res = await getCartList()
        const allItems = res.data || []
        // Filter by cartIds
        this.checkoutItems = allItems.filter(item => cartIds.includes(item.id)).map(item => ({
          id: item.id,
          itemId: item.itemId,
          title: item.itemName,
          image: item.itemImage,
          price: item.currentPrice,
          quantity: item.quantity,
          productType: item.productType, // Need to ensure API returns this
          itemType: item.itemType
        }))

        if (this.checkoutItems.length === 0) {
           this.$message.warning('商品无效或已删除')
           this.$router.push('/cart')
           return
        }

        // Fetch addresses if needed
        if (this.hasPhysicalItems) {
          this.fetchAddresses()
        }
      } catch (err) {
        this.$message.error('加载商品失败')
      } finally {
        this.loading = false
      }
    },
    async fetchAddresses() {
      try {
        const res = await getMyAddresses()
        const data = res.data
        const list = Array.isArray(data) ? data : (data && (data.list || []) || [])
        this.addresses = list.map(a => ({
          id: a.id,
          name: a.receiverName || a.name,
          phone: a.receiverPhone || a.phone,
          province: a.province,
          city: a.city,
          district: a.district,
          detail: a.detail || a.detailAddress
        }))
        // Set default
        if (this.addresses.length > 0) {
          const def = list.find(a => a.isDefault)
          this.selectedAddressId = def ? def.id : this.addresses[0].id
        }
      } catch (e) {
        console.error(e)
      }
    },
    goToAddressManage() {
      this.$router.push('/member-center/address')
    },
    removeItem(id) {
      this.$confirm('确定要移除该商品吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.checkoutItems = this.checkoutItems.filter(item => item.id !== id)
        // 更新 localStorage 中的 cartIds
        const remainingIds = this.checkoutItems.map(item => item.id)
        localStorage.setItem('checkoutCartIds', JSON.stringify(remainingIds))
        
        if (this.checkoutItems.length === 0) {
          this.$message.warning('没有商品了')
          this.$router.push('/cart')
        }
      }).catch(() => {})
    },
    async submitOrder() {
      if (this.hasPhysicalItems && !this.selectedAddressId) {
        this.$message.warning('请选择收货地址')
        return
      }
      if (this.payType !== 1 && this.payType !== 2) this.payType = 1

      let receiverInfo = {}
      if (this.hasPhysicalItems) {
        const addr = this.addresses.find(a => a.id === this.selectedAddressId)
        if (addr) {
          receiverInfo = {
            receiverName: addr.name,
            receiverPhone: addr.phone,
            receiverAddress: `${addr.province}${addr.city}${addr.district}${addr.detail}`
          }
        }
      }

      const payload = {
        cartIds: this.checkoutItems.map(item => item.id),
        payType: this.payType,
        ...receiverInfo,
        // Optional fields
        cashAmount: this.totalAmount, // Assuming full cash payment for now
        coinAmount: 0,
        couponAmount: 0
      }

      this.submitting = true
      try {
        const res = await checkoutCart(payload)
        const data = res && res.data != null ? res.data : null
        let orderNo = null
        if (data && typeof data === 'object') {
          orderNo = data.orderNo || data.order_no || data.outTradeNo || data.out_trade_no || data.orderId || data.id
          if (!orderNo && Array.isArray(data.orderNos) && data.orderNos.length > 0) orderNo = data.orderNos[0]
          if (!orderNo && Array.isArray(data.orderNosList) && data.orderNosList.length > 0) orderNo = data.orderNosList[0]
        } else if (typeof data === 'string') {
          orderNo = data
        } else if (Array.isArray(data) && data.length > 0) {
          orderNo = data[0]
        }

        this.$message.success('订单提交成功')

        const firstItem = this.checkoutItems && this.checkoutItems.length > 0 ? this.checkoutItems[0] : null
        const bizType = firstItem && Number(firstItem.itemType) === 2 ? 'product' : 'course'
        const payId = firstItem && firstItem.itemId != null ? firstItem.itemId : null
        if (payId && orderNo) {
          this.$router.push({
            name: 'payment',
            params: { id: payId },
            query: {
              orderNo: String(orderNo),
              bizType,
              payType: this.payType,
              totalAmount: this.totalAmount,
              title: firstItem ? firstItem.title : '',
              image: firstItem ? firstItem.image : '',
              price: firstItem ? firstItem.price : '',
              quantity: firstItem ? firstItem.quantity : ''
            }
          })
          return
        }
        this.$router.push('/member-center/orders')
      } catch (err) {
        this.$message.error(err.message || '提交失败')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding-top: 20px;
  padding-bottom: 40px;
}

.page-inner-box {
  max-width: 1200px;
  margin: 0 auto;
  background-color: #fff;
  border-radius: 8px;
  padding: 30px;
}

.page-title {
  font-size: 20px;
  margin-bottom: 30px;
  color: #333;
}

.section-box {
  margin-bottom: 30px;
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 15px;
  padding-left: 10px;
  border-left: 4px solid #3E2CCD;
}

.address-list {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.address-item {
  width: 240px;
  border: 1px solid #e0e0e0;
  padding: 15px;
  cursor: pointer;
  border-radius: 4px;
  position: relative;
  
  &.active {
    border-color: #3E2CCD;
    background-color: #f0f9ff;
  }
  
  .addr-name {
    font-weight: bold;
    margin-bottom: 5px;
  }
  
  .addr-detail {
    font-size: 12px;
    color: #666;
    line-height: 1.4;
  }
  
  .check-icon {
    position: absolute;
    bottom: 5px;
    right: 5px;
    color: #3E2CCD;
  }
  
  &.add-new {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #999;
    
    i {
      font-size: 24px;
      margin-bottom: 5px;
    }
  }
}

.order-items {
  border: 1px solid #eee;
  border-radius: 4px;
}

.order-item {
  display: flex;
  padding: 15px;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
  
  .item-img {
    width: 80px;
    height: 60px;
    object-fit: cover;
    margin-right: 15px;
  }
  
  .item-info {
    flex: 1;
    
    .item-name {
      font-size: 14px;
      margin-bottom: 5px;
    }
    
    .item-price {
      color: #999;
      font-size: 12px;
    }
  }
  
  .item-total {
    font-weight: bold;
    color: #f56c6c;
  }
  
  .item-remove {
    margin-left: 15px;
    cursor: pointer;
    color: #999;
    
    &:hover {
      color: #f56c6c;
    }
    
    i {
      font-size: 18px;
    }
  }
}

.pay-methods {
  display: flex;
  gap: 20px;
  
  .pay-method {
    display: flex;
    align-items: center;
    padding: 10px 20px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    cursor: pointer;
    
    &.active {
      border-color: #3E2CCD;
      color: #3E2CCD;
    }
    
    img {
      width: 24px;
      height: 24px;
      margin-right: 10px;
    }
  }
}

.checkout-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid #eee;
  
  .price-info {
    margin-right: 20px;
    font-size: 14px;
    
    .total-price {
      font-size: 24px;
      color: #f56c6c;
      font-weight: bold;
    }
  }
}
</style>
